import { useEffect, useMemo, useReducer, useRef } from 'react';
import type { GameOutcome, RunMode } from '../games/core/types';
import type { NonogramPuzzle } from '../games/nonogram/types';
import type { RunRepository } from '../persistence/runRepository';
import {
  generateRunInWorker,
  type GenerationJob,
  type GenerationProgress,
} from '../workers/generatorClient';
import { completeFromOutcome, createChargeId, createEmptyRun, runReducer } from './runReducer';
import { hydrateRun, snapshotRun } from './serialization';
import { systemClock, type Clock } from '../shared/time';
import type { RunState } from './types';

export type RunController = {
  run: RunState | null;
  start: (seed: string, mode: RunMode, recentNonograms?: NonogramPuzzle[]) => void;
  resume: (saved: RunState) => void;
  splashComplete: () => void;
  saveGameState: (state: unknown) => void;
  charge: (key: string, amountMs: number, label: string) => void;
  complete: (outcome: GameOutcome) => void;
  skip: (penaltyMs: number) => void;
  outcomeComplete: () => void;
  loadingComplete: () => void;
  cancelPreparation: () => Promise<void>;
  retryPreparation: () => void;
  abandon: () => Promise<void>;
};

export function useRunController(
  repository: RunRepository,
  clock: Clock = systemClock,
): RunController {
  const [state, dispatch] = useReducer(runReducer, undefined, createEmptyRun);
  const hasRun = Boolean(state.id);
  const stateRef = useRef(state);
  const recentNonogramsRef = useRef<NonogramPuzzle[]>([]);
  const generationJobRef = useRef<GenerationJob | null>(null);
  const discardingRef = useRef(false);
  stateRef.current = state;

  useEffect(() => {
    if (!hasRun || state.phase !== 'preparing' || state.puzzles.length > 0) return;
    const job = generateRunInWorker(
      state.seed,
      (progress: GenerationProgress) => {
        dispatch({ type: 'generation-progress', ...progress, nowEpoch: clock.epoch() });
      },
      recentNonogramsRef.current,
    );
    generationJobRef.current = job;
    void job.promise
      .then((puzzles) => {
        if (generationJobRef.current === job) generationJobRef.current = null;
        dispatch({
          type: 'prepared',
          puzzles,
          nowEpoch: clock.epoch(),
          nowPerformance: clock.monotonic(),
        });
      })
      .catch((error: unknown) => {
        if (generationJobRef.current === job) generationJobRef.current = null;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        dispatch({
          type: 'fail',
          message: error instanceof Error ? error.message : 'Puzzle preparation failed.',
          nowEpoch: clock.epoch(),
          nowPerformance: clock.monotonic(),
        });
      });
    return () => {
      job.cancel();
      if (generationJobRef.current === job) generationJobRef.current = null;
    };
  }, [clock, hasRun, state.id, state.phase, state.puzzles.length, state.seed]);

  useEffect(() => {
    if (!hasRun) return;
    const handle = window.setTimeout(() => {
      if (discardingRef.current) return;
      void repository.save(snapshotRun(stateRef.current, clock.monotonic()));
    }, 30);
    return () => window.clearTimeout(handle);
  }, [clock, hasRun, repository, state]);

  useEffect(() => {
    if (!hasRun) return;
    const checkpoint = () => {
      if (discardingRef.current) return;
      void repository.save(snapshotRun(stateRef.current, clock.monotonic()));
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') checkpoint();
    };
    window.addEventListener('pagehide', checkpoint);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      checkpoint();
      window.removeEventListener('pagehide', checkpoint);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clock, hasRun, repository]);

  return useMemo(
    () => ({
      run: hasRun ? state : null,
      start(seed: string, mode: RunMode, recentNonograms: NonogramPuzzle[] = []) {
        discardingRef.current = false;
        recentNonogramsRef.current = recentNonograms;
        dispatch({
          type: 'create',
          id: crypto.randomUUID(),
          seed,
          mode,
          nowEpoch: clock.epoch(),
          nowPerformance: clock.monotonic(),
        });
      },
      resume(saved: RunState) {
        discardingRef.current = false;
        dispatch({ type: 'hydrate', state: hydrateRun(saved, clock.monotonic()) });
      },
      splashComplete() {
        dispatch({
          type: 'splash-complete',
          nowEpoch: clock.epoch(),
          nowPerformance: clock.monotonic(),
        });
      },
      saveGameState(gameState: unknown) {
        dispatch({ type: 'save-game-state', state: gameState, nowEpoch: clock.epoch() });
      },
      charge(key: string, amountMs: number, label: string) {
        dispatch({
          type: 'charge',
          id: createChargeId(stateRef.current, key),
          amountMs,
          label,
          nowEpoch: clock.epoch(),
        });
      },
      complete(outcome: GameOutcome) {
        dispatch(completeFromOutcome(outcome, clock.epoch(), clock.monotonic()));
      },
      skip(penaltyMs: number) {
        dispatch(
          completeFromOutcome(
            { kind: 'skipped', baseDeltaMs: penaltyMs, message: 'Skipped — moving on.' },
            clock.epoch(),
            clock.monotonic(),
          ),
        );
      },
      outcomeComplete() {
        dispatch({ type: 'outcome-complete', nowEpoch: clock.epoch() });
      },
      loadingComplete() {
        dispatch({
          type: 'loading-complete',
          nowEpoch: clock.epoch(),
          nowPerformance: clock.monotonic(),
        });
      },
      async cancelPreparation() {
        const runId = stateRef.current.id;
        discardingRef.current = true;
        generationJobRef.current?.cancel();
        generationJobRef.current = null;
        dispatch({ type: 'discard' });
        if (runId) await repository.delete(runId);
      },
      retryPreparation() {
        dispatch({ type: 'retry-generation', nowEpoch: clock.epoch() });
      },
      async abandon() {
        const runId = stateRef.current.id;
        discardingRef.current = true;
        generationJobRef.current?.cancel();
        generationJobRef.current = null;
        dispatch({ type: 'discard' });
        if (runId) await repository.delete(runId);
      },
    }),
    [clock, hasRun, repository, state],
  );
}
